// src/Admin/Awards.js
/* =========================================================
   Admin › Badges: give students and teachers a badge or a medal
   ("Student of the month", "Always on time", "Teacher of the month", …).
   The person gets a bell, the badge shows in their badges, and everyone
   sees it on the "Wall of fame" of the student and teacher dashboards.
   API: /api/admin/awards (catalog, people, list, give, remove)
   ========================================================= */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAward,
  faUserGraduate,
  faChalkboardUser,
  faMagnifyingGlass,
  faTrashCan,
  faTriangleExclamation,
  faCircleCheck,
} from '@fortawesome/free-solid-svg-icons';
import { BADGE_ICON } from '../components/Competitions/BadgeShelf';
import './Awards.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthText = (p) => {
  const m = /^(\d{4})-(\d{2})$/.exec(p || '');
  return m ? new Date(+m[1], +m[2] - 1, 1).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '';
};

/* Ready messages for each badge ({name} = first name). Click one to fill the message box. */
const MESSAGES = {
  student_month: ['Amazing month, {name}! Great work in every class 🌟', 'Well done {name}! Hard work, great projects and a big smile in every class.', 'Proud of you {name}, keep shining! ⭐'],
  on_time: ['Always on time, {name}! Thank you for being ready for every class ⏰', 'Never late this month, well done {name}!'],
  perfect_attendance: ['Not a single class missed this month. Great job {name}! 📅', 'Every class, every week: well done {name}!'],
  creative: ['What an amazing project, {name}! Your idea was so creative 💡', 'Your project made everyone smile, well done {name}!'],
  hard_worker: ['Great effort in every class, {name}! Keep going 💪', 'Your hard work shows, {name}. Proud of you!'],
  most_improved: ['Huge progress this month, {name}! Keep it up 📈', 'Look how far you’ve come, {name}! Well done.'],
  teacher_month: ['Thank you {name} for an amazing month with your students! ⭐', 'Great classes, happy students and parents. Thank you {name}!'],
  t_on_time: ['Always on time, thank you {name}! ⏰', 'Every class started on time this month. Thank you {name}!'],
  parents_favorite: ['Parents love your classes, {name}! Thank you ❤️', 'The best feedback from parents this month. Well done {name}!'],
  feedback_hero: ['Every class feedback on time, thank you {name}! ✍️', 'Parents always know how the class went thanks to you, {name}.'],
  renewals: ['Your students keep coming back. Thank you {name}! 🔁', 'So many renewals this month: your classes make the difference, {name}!'],
};
const firstOf = (name) => {
  const f = String(name || '').trim().split(/\s+/)[0] || '';
  return f.charAt(0).toUpperCase() + f.slice(1);
};

const Face = ({ src, name }) => {
  const [broken, setBroken] = useState(false);
  return <span className="rmaaw-face">{src && !broken ? <img src={src} alt="" onError={() => setBroken(true)} /> : String(name || '?').charAt(0)}</span>;
};

const AdminAwards = () => {
  const [catalog, setCatalog] = useState(null);
  const [people, setPeople] = useState(null);
  const [list, setList] = useState(null);
  const [tableReady, setTableReady] = useState(true);
  const [error, setError] = useState('');

  const [role, setRole] = useState('student');
  const [badge, setBadge] = useState('');
  const [person, setPerson] = useState(null);
  const [search, setSearch] = useState('');
  const [onlyActive, setOnlyActive] = useState(true);
  const [period, setPeriod] = useState(thisMonth);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState('');
  const [suggest, setSuggest] = useState(null); // { period, student: { badge: [...] }, teacher: {...} }

  const loadList = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/api/admin/awards`, auth());
      setList(res.data?.awards || []);
      setTableReady(res.data?.tableReady !== false);
    } catch (err) {
      setList([]);
      setToast(err.response?.data?.error || 'Couldn’t load the badges given.');
    }
  }, []);

  useEffect(() => {
    Promise.all([axios.get(`${API}/api/admin/awards/catalog`, auth()), axios.get(`${API}/api/admin/awards/people`, auth())])
      .then(([c, p]) => {
        setCatalog(c.data);
        setPeople(p.data);
      })
      .catch((err) => setError(err.response?.status === 404 ? 'Update the server to use this page (new backend files).' : 'We couldn’t load the page.'));
    loadList();
  }, [loadList]);

  // suggestions for the chosen month (who deserves each badge, from the website's data)
  const loadSuggest = useCallback(async (month) => {
    setSuggest(null);
    try {
      const res = await axios.get(`${API}/api/admin/awards/suggest?month=${month}`, auth());
      setSuggest(res.data);
    } catch {
      setSuggest({ period: month, failed: true });
    }
  }, []);
  useEffect(() => {
    if (/^\d{4}-\d{2}$/.test(period)) loadSuggest(period);
  }, [period, loadSuggest]);

  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(''), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const badges = catalog?.[role] || [];
  const pool = useMemo(() => (people ? (role === 'student' ? people.students : people.teachers) : []), [people, role]);
  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return pool.filter((p) => (!onlyActive || p.active) && (!q || p.name.toLowerCase().includes(q))).slice(0, 40);
  }, [pool, search, onlyActive]);

  const switchRole = (r) => {
    setRole(r);
    setBadge('');
    setPerson(null);
    setSearch('');
  };

  const give = async () => {
    if (!badge || !person) return;
    const b = badges.find((x) => x.key === badge);
    if (!window.confirm(`Give “${b?.title}” (${monthText(period)}) to ${person.name}?\nThey get a notification, and it shows on the Wall of fame.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/api/admin/awards`, { role, id: person.id, badge, period, note }, auth());
      setToast(`${b?.title} given to ${person.name} 🎉`);
      setPerson(null);
      setSearch('');
      setNote('');
      loadList();
    } catch (err) {
      setToast(err.response?.data?.error || 'Couldn’t give the badge.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Take back “${a.title}” from ${a.name}?`)) return;
    try {
      await axios.delete(`${API}/api/admin/awards/${a.id}`, auth());
      setList((l) => l.filter((x) => x.id !== a.id));
    } catch {
      setToast('Couldn’t remove it.');
    }
  };

  if (error) {
    return (
      <div className="rmaaw">
        <p className="rmaaw-alert">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
        </p>
      </div>
    );
  }

  return (
    <div className="rmaaw">
      <header className="rmaaw-header">
        <span className="rmaaw-eyebrow">
          <FontAwesomeIcon icon={faAward} /> Badges & medals
        </span>
        <h1>Give a badge</h1>
        <p>The student or teacher gets a notification, the badge shows in their badges, and everyone sees it on the Wall of fame of the dashboards.</p>
      </header>

      {!tableReady && (
        <p className="rmaaw-alert">
          <FontAwesomeIcon icon={faTriangleExclamation} /> Run <code>backend/sql/AwardedBadges.sql</code> on the database first.
        </p>
      )}

      <section className="rmaaw-card" aria-label="Give a badge">
        <div className="rmaaw-seg" role="radiogroup" aria-label="For">
          <button type="button" role="radio" aria-checked={role === 'student'} className={role === 'student' ? 'is-on' : ''} onClick={() => switchRole('student')}>
            <FontAwesomeIcon icon={faUserGraduate} /> Student
          </button>
          <button type="button" role="radio" aria-checked={role === 'teacher'} className={role === 'teacher' ? 'is-on' : ''} onClick={() => switchRole('teacher')}>
            <FontAwesomeIcon icon={faChalkboardUser} /> Teacher
          </button>
        </div>

        <h2 className="rmaaw-step">1. Badge</h2>
        {!catalog ? (
          <div className="rmaaw-skel" />
        ) : (
          <ul className="rmaaw-badges" role="radiogroup" aria-label="Badge">
            {badges.map((b) => (
              <li key={b.key}>
                <button type="button" role="radio" aria-checked={badge === b.key} className={`is-${b.key} ${badge === b.key ? 'is-on' : ''}`} onClick={() => setBadge(b.key)}>
                  <span className="rmaaw-icon">
                    <FontAwesomeIcon icon={BADGE_ICON[b.icon] || faAward} />
                  </span>
                  <strong>{b.title}</strong>
                  <small>{b.how}</small>
                  {suggest?.[role]?.[b.key]?.length > 0 && <em className="rmaaw-sugcount">{suggest[role][b.key].length} suggested</em>}
                </button>
              </li>
            ))}
          </ul>
        )}

        <h2 className="rmaaw-step">2. {role === 'student' ? 'Student' : 'Teacher'}</h2>
        {person ? (
          <div className="rmaaw-picked">
            <Face src={person.photo} name={person.name} />
            <b>{person.name}</b>
            {person.level && <span className="rmaaw-tag">{person.level}</span>}
            <button type="button" className="rmaaw-link" onClick={() => setPerson(null)}>
              Change
            </button>
          </div>
        ) : (
          <>
            {badge && (
              <div className="rmaaw-suggest">
                <h3>
                  Suggested for {monthText(period)}
                  {!suggest && <span className="rmaaw-dim"> · looking…</span>}
                </h3>
                {suggest?.failed && <p className="rmaaw-dim">Suggestions need the latest server update.</p>}
                {suggest && !suggest.failed && !(suggest[role]?.[badge] || []).length && (
                  <p className="rmaaw-dim">Nobody stands out from the data for this one. Search below to pick someone yourself.</p>
                )}
                {(suggest?.[role]?.[badge] || []).length > 0 && (
                  <ul className="rmaaw-sugs">
                    {suggest[role][badge].map((p) => (
                      <li key={p.id}>
                        <button type="button" onClick={() => setPerson(p)}>
                          <Face src={p.photo} name={p.name} />
                          <span className="rmaaw-sugtext">
                            <b>{p.name}</b>
                            <small>{p.why}</small>
                          </span>
                          {p.level && <i className="rmaaw-tag">{p.level}</i>}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                {badge === 'on_time' && <p className="rmaaw-dim rmaaw-small">Only joins from the dashboard count: students who open Zoom directly can’t be seen.</p>}
              </div>
            )}
            <div className="rmaaw-searchrow">
              <label className="rmaaw-search">
                <FontAwesomeIcon icon={faMagnifyingGlass} />
                <input type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search a ${role}`} aria-label={`Search a ${role}`} />
              </label>
              {role === 'student' && (
                <label className="rmaaw-check">
                  <input type="checkbox" checked={onlyActive} onChange={(e) => setOnlyActive(e.target.checked)} /> Only current students
                </label>
              )}
            </div>
            {!people ? (
              <div className="rmaaw-skel" />
            ) : (
              <ul className="rmaaw-people">
                {matches.map((p) => (
                  <li key={p.id}>
                    <button type="button" onClick={() => setPerson(p)}>
                      <Face src={p.photo} name={p.name} />
                      <span>{p.name}</span>
                      {p.level && <small>{p.level}</small>}
                    </button>
                  </li>
                ))}
                {!matches.length && <li className="rmaaw-dim">Nobody found.</li>}
              </ul>
            )}
          </>
        )}

        <h2 className="rmaaw-step">3. Month and message</h2>
        <div className="rmaaw-row">
          <label className="rmaaw-field">
            <span>Month</span>
            <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} />
          </label>
          <label className="rmaaw-field rmaaw-field--wide">
            <span>Message (optional, everyone sees it)</span>
            <input type="text" maxLength={300} value={note} onChange={(e) => setNote(e.target.value)} placeholder="Amazing game project, well done!" />
          </label>
        </div>
        {badge && (MESSAGES[badge] || []).length > 0 && (
          <div className="rmaaw-msgs" aria-label="Ready messages">
            <span>Ready messages:</span>
            {MESSAGES[badge].map((t) => {
              const text = t.replace('{name}', firstOf(person?.name) || 'champ');
              return (
                <button key={t} type="button" className={note === text ? 'is-on' : ''} onClick={() => setNote(text)}>
                  {text}
                </button>
              );
            })}
          </div>
        )}

        <button type="button" className="rmaaw-btn" disabled={!badge || !person || busy || !tableReady} onClick={give}>
          <FontAwesomeIcon icon={faAward} /> {busy ? 'Giving…' : 'Give the badge'}
        </button>
      </section>

      <section className="rmaaw-card" aria-label="Given lately">
        <h2 className="rmaaw-step">Given lately</h2>
        {!list ? (
          <div className="rmaaw-skel" />
        ) : list.length === 0 ? (
          <p className="rmaaw-dim">No badge given yet.</p>
        ) : (
          <ul className="rmaaw-given">
            {list.map((a) => (
              <li key={a.id}>
                <Face src={a.photo} name={a.name} />
                <div>
                  <b>{a.name}</b> <span className="rmaaw-tag">{a.role === 'teacher' ? 'Teacher' : 'Student'}</span>
                  <p>
                    <FontAwesomeIcon icon={BADGE_ICON[a.icon] || faAward} /> {a.title}
                    {a.periodLabel ? ` · ${a.periodLabel}` : ''}
                    {a.note ? ` · “${a.note}”` : ''}
                  </p>
                </div>
                <button type="button" className="rmaaw-del" onClick={() => remove(a)} aria-label={`Take back ${a.title} from ${a.name}`}>
                  <FontAwesomeIcon icon={faTrashCan} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {toast && (
        <div className="rmaaw-toast" role="status">
          <FontAwesomeIcon icon={faCircleCheck} /> {toast}
        </div>
      )}
    </div>
  );
};

export default AdminAwards;
